import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import styles from "./App.module.css";

import Dashboard from "./pages/dashboard/Dashboard";
import Users from "./pages/users/Users";
import Orders from "./pages/orders/Orders";
import OrderDetail from "./pages/orders/OrderDetail";
import Invoices from "./pages/invoices/Invoices";
import InvoiceDetail from "./pages/invoices/InvoiceDetail";
import Parts from "./pages/parts/Parts";
import PartDetail from "./pages/parts/PartDetail";

// Component to handle root redirect based on auth status
function RootRedirect() {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  } else {
    return <Navigate to="/login" replace />;
  }
}

// Component to handle all other routes (direct links)
function CatchAllRoute() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  // If user is authenticated and trying to access a non-existent route,
  // show a 404 page within the layout
  if (isAuthenticated) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className={styles.notFoundContainer}>
            <h1 className={styles.notFoundTitle}>404 - Page Not Found</h1>
            <p className={styles.notFoundMessage}>
              The page <code className={styles.code}>{location.pathname}</code> does not exist.
            </p>
            <button onClick={() => window.history.back()} className={styles.backButton}>
              Go Back
            </button>
          </div>
        </Layout>
      </ProtectedRoute>
    );
  }

  // If not authenticated, redirect to login with return url
  return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

// Separate component for routes that can use the AuthContext
function AppRoutes() {
  return (
    <Routes>
      {/* Public routes - only accessible when not logged in */}
      <Route
        path="/login"
        element={
          <ProtectedRoute requireAuth={false}>
            <Login />
          </ProtectedRoute>
        }
      />
      <Route
        path="/register"
        element={
          <ProtectedRoute requireAuth={false}>
            <Register />
          </ProtectedRoute>
        }
      />
      {/* Protected routes with Layout */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* EXAMPLES OF PROTECTED ROUTES WITH ROLES/PERMISSIONS — Admins can access user management */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRole="admin">
            <Layout>{<Users /> }</Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parts"
        element={
          <ProtectedRoute requiredPermission="component_orders:read">
            <Layout>
              <Parts />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/parts/:partId"
        element={
          <ProtectedRoute requiredPermission="component_orders:read">
            <Layout>
              <PartDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute requiredPermission="component_orders:read">
            <Layout>
              <Orders />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute requiredPermission="component_orders:read">
            <Layout>
              <Invoices />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:invoiceId"
        element={
          <ProtectedRoute requiredPermission="component_orders:read">
            <Layout>
              <InvoiceDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Users with create permission can access order creation */}
      <Route
        path="/orders/new"
        element={
          <ProtectedRoute requiredPermission="component_orders:create">
            <Layout>{/* <CreateOrder /> */}</Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:orderId"
        element={
          <ProtectedRoute requiredPermission="component_orders:read">
            <Layout>
              <OrderDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Managers and admins can access reports */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute requiredAnyRole={["admin", "manager"]}>
            <Layout>{/* <Reports /> */}</Layout>
          </ProtectedRoute>
        }
      />
      {/* END EXAMPLES OF PROTECTED ROUTES WITH ROLES/PERMISSIONS */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>{/* <ProfilePage /> */}</Layout>
          </ProtectedRoute>
        }
      />
      {/* Redirect root based on authentication status */}
      <Route path="/" element={<RootRedirect />} />
      {/* Catch all route - handles direct links to any protected route */}
      <Route path="*" element={<CatchAllRoute />} />
    </Routes>
  );
}

export default App;
